// src/services/mockApiService.js
//
// Drop-in replacement for src/services/api.js.
// Exposes the same `.get(endpoint, params, options)` interface so hooks need
// zero logic changes — only the import source differs.
//
// Toggle via .env:  REACT_APP_USE_MOCK_API=true

import {
  MOCK_COLD_CALENDAR_TITLE,
  MOCK_COLD_CALENDAR_DESCRIPTION,
  MOCK_COLD_CALENDAR_ACCOUNT,
  MOCK_FILTER_OPTIONS,
  MOCK_LAST_UPDATED,
  MOCK_CAMPAIGNS,
  MOCK_SUMMARY,
  MOCK_CONFIGURATIONS_RESPONSE,
  MOCK_CONFIGURATIONS,
  MOCK_BOOKINGS,
} from './mockData';

// Simulated network latency in ms. Adjust to taste.
const DELAY_MIN = 300;
const DELAY_MAX = 700;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const randomDelay = () =>
  delay(Math.floor(Math.random() * (DELAY_MAX - DELAY_MIN) + DELAY_MIN));

// ─── Exact-match route table ───────────────────────────────────────────────────

const ROUTES = {
  // Cold Calendar
  '/cold-calendar/performance-by-title': () => ({
    status: 'success',
    data: MOCK_COLD_CALENDAR_TITLE,
  }),
  '/cold-calendar/performance-by-description': () => ({
    status: 'success',
    data: MOCK_COLD_CALENDAR_DESCRIPTION,
  }),
  '/cold-calendar/performance-by-account': () => ({
    status: 'success',
    data: MOCK_COLD_CALENDAR_ACCOUNT,
  }),
  '/cold-calendar/filter-options': () => ({
    status: 'success',
    data: MOCK_FILTER_OPTIONS,
  }),
  '/cold-calendar/data-synced': () => ({
    status: 'success',
    data: { data_last_updated: MOCK_LAST_UPDATED },
  }),

  // Webinar Funnel (used by useCampaignData via mockFetch)
  '/analytics/campaigns': () => MOCK_CAMPAIGNS,
  '/analytics/summary':   () => MOCK_SUMMARY,

  // Configurations — full list
  '/configurations': () => MOCK_CONFIGURATIONS_RESPONSE,

  // Config health check
  '/configurations/health': () => ({ status: 'ok', source: 'mock' }),
};

// ─── Core .get() method ───────────────────────────────────────────────────────

const get = async (endpoint, params = {}, options = {}) => {
  const { signal } = options;

  await randomDelay();

  if (signal?.aborted) {
    const err = new Error('Request aborted');
    err.name = 'AbortError';
    throw err;
  }

  // Exact match first
  if (ROUTES[endpoint]) return ROUTES[endpoint](params);

  // Single-key config lookup: /configurations/:key
  const configKeyMatch = endpoint.match(/^\/configurations\/(.+)$/);
  if (configKeyMatch) {
    const key = configKeyMatch[1];
    const config = MOCK_CONFIGURATIONS[key];
    if (config) {
      return { success: true, key, ...config };
    }
    // Return a not-found shape so callers that catch can create it
    const err = new Error(`Configuration key not found: ${key}`);
    err.status = 404;
    throw err;
  }

  console.warn(`[mockApiService] No handler for endpoint: ${endpoint}`);
  throw new Error(`Mock endpoint not found: ${endpoint}`);
};

// ─── .post() / .put() / .delete() stubs ──────────────────────────────────────
// configService calls these for write operations. In mock mode we just
// acknowledge the request and return success so the UI doesn't crash.

const post = async (endpoint, _body = {}) => {
  await randomDelay();
  console.info(`[mockApiService] POST ${endpoint} — acknowledged (no-op in mock mode)`);
  return { success: true, source: 'mock' };
};

const put = async (endpoint, _body = {}) => {
  await randomDelay();
  console.info(`[mockApiService] PUT ${endpoint} — acknowledged (no-op in mock mode)`);
  return { success: true, source: 'mock' };
};

const del = async (endpoint) => {
  await randomDelay();
  console.info(`[mockApiService] DELETE ${endpoint} — acknowledged (no-op in mock mode)`);
  return { success: true, source: 'mock' };
};

// ─── mockFetch — wraps .get() in a fetch-like Response shape ──────────────────
// Used by useCampaignData which calls the native fetch() API directly.

export const mockFetch = async (url, _options = {}) => {
  const urlObj = new URL(url, 'http://localhost');
  const endpoint = urlObj.pathname;
  const data = await get(endpoint);
  return {
    ok: true,
    status: 200,
    json: async () => data,
  };
};

// ─── Default export — same shape as real apiService ───────────────────────────

const mockApiService = { get, post, put, delete: del };

export default mockApiService;